import React, { Suspense, lazy } from "react"
import { Routes, Route, Navigate } from "react-router-dom"
import SuspenseFallback from "../Components/SuspenseFallback/SuspenseFallback"
import NotFound from "../Pages/NotFound/NotFound"
import NotAuthorized from "../Pages/NotFound/NotAuthorized"
import ProtectedRoute from "./ProtectedRoute"
import { PERMISSION_KEYS } from "../Utils/Constants/common"
import {
  ROOT,
  DASHBOARD,
  CAMERAS,
  OCCUPANCY,
  DEVICES,
  EDIT_DEVICE,
  SITE_MAP,
  NOTIFICATIONS,
  ANALYTICS,
  ACCESS_CONTROL,
  SETTINGS,
  HELP,
  ADD_DEVICE,
  CREATE_ORGANIZATION,
  NVR_CAMERA_SYNC,
  DELETED_DEVICES,
  CATALOGS,
  CATALOG_DEVICE_CATEGORIES,
  CATALOG_DEVICE_MODEL_GROUPS,
  CATALOG_DEVICE_MODELS,
  MEMBERS,
  UNAUTHORIZED,
} from "./CONSTANTS"

const Layout = lazy(() =>
  import(/* webpackChunkName: "Layout" */ "../Pages/Layout/Layout").then(
    (m) => ({
      default: m.Layout,
    }),
  ),
)

const Dashboard = lazy(
  () =>
    import(/* webpackChunkName: "Dashboard" */ "../Pages/Dashboard/Dashboard"),
)

const CamerasModule = lazy(
  () =>
    import(
      /* webpackChunkName: "CamerasModule" */ "./Layouts/ModuleLayouts/Cameras"
    ),
)

const Occupancy = lazy(
  () =>
    import(/* webpackChunkName: "Occupancy" */ "../Pages/Occupancy/Occupancy"),
)

const Devices = lazy(
  () => import(/* webpackChunkName: "Devices" */ "../Pages/Devices/Devices"),
)

const EditDevice = lazy(
  () =>
    import(/* webpackChunkName: "EditDevice" */ "../Pages/Devices/EditDevice"),
)

const SiteMap = lazy(
  () => import(/* webpackChunkName: "SiteMap" */ "../Pages/SiteMap/SiteMap"),
)

const Notifications = lazy(
  () =>
    import(
      /* webpackChunkName: "Notifications" */ "../Pages/Notifications/Notifications"
    ),
)

const Analytics = lazy(
  () =>
    import(/* webpackChunkName: "Analytics" */ "../Pages/Analytics/Analytics"),
)

const AccessControl = lazy(
  () =>
    import(
      /* webpackChunkName: "AccessControl" */ "../Pages/AccessControl/AccessControl"
    ),
)

const SettingsModule = lazy(
  () =>
    import(
      /* webpackChunkName: "SettingsModule" */ "./Layouts/ModuleLayouts/Settings"
    ),
)

const Help = lazy(
  () => import(/* webpackChunkName: "Help" */ "../Pages/Help/Help"),
)

const AddDevice = lazy(
  () => import(/* webpackChunkName: "AddDevice" */ "../Pages/AddDevices/Index"),
)

const CreateOrg = lazy(() =>
  import(
    /* webpackChunkName: "CreateOrg" */ "../Pages/CreateOrganization/CreateOrg"
  ).then((m) => ({
    default: m.CreateOrg,
  })),
)

const NvrCameraSync = lazy(
  () =>
    import(
      /* webpackChunkName: "NvrCameraSync" */ "../Pages/Devices/NvrCameraSync"
    ),
)

const DeletedDevices = lazy(
  () =>
    import(
      /* webpackChunkName: "DeletedDevices" */ "../Pages/Settings/Decommissioned/DeletedDevices"
    ),
)

const CatalogsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CatalogsPage" */ "../Pages/Catalogs/CatalogsPage"
    ),
)

const DeviceCategoriesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "DeviceCategoriesPage" */ "../Pages/Catalogs/DeviceCategoriesPage"
    ),
)

const DeviceModelGroupsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "DeviceModelGroupsPage" */ "../Pages/Catalogs/DeviceModelGroupsPage"
    ),
)

const DeviceModelsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "DeviceModelsPage" */ "../Pages/Catalogs/DeviceModelsPage"
    ),
)
const Members = lazy(
  () =>
    import(
      /* webpackChunkName: "Members" */ "../Pages/Settings/Members/Members"
    ),
)

const RouterConfig = () => {
  return (
    <Routes>
      <Route
        path={CREATE_ORGANIZATION}
        element={
          <Suspense fallback={<SuspenseFallback />}>
            <CreateOrg />
          </Suspense>
        }
      />

      <Route
        path={ROOT}
        element={
          <Suspense fallback={<SuspenseFallback />}>
            <Layout />
          </Suspense>
        }
      >
        <Route
          index={true}
          element={<Navigate to={DASHBOARD} replace={true} />}
        />
        <Route path={DASHBOARD} element={<Dashboard />} />

        <Route
          path={`${CAMERAS}/*`}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.VIDEO_LIVE}>
              <Suspense fallback={<SuspenseFallback />}>
                <CamerasModule />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path={OCCUPANCY}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.ANALYTICS_VIEW}>
              <Occupancy />
            </ProtectedRoute>
          }
        />
        <Route
          path={DEVICES}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.DEVICES_VIEW}>
              <Devices />
            </ProtectedRoute>
          }
        />
        <Route
          path={EDIT_DEVICE}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.DEVICES_MANAGE}>
              <Suspense fallback={<SuspenseFallback />}>
                <EditDevice />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path={SITE_MAP}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.DEVICES_VIEW}>
              <SiteMap />
            </ProtectedRoute>
          }
        />
        <Route
          path={NOTIFICATIONS}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.EVENTS_VIEW_OPERATIONAL}>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path={ANALYTICS}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.ANALYTICS_VIEW}>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path={ACCESS_CONTROL}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.USERS_MANAGE}>
              <AccessControl />
            </ProtectedRoute>
          }
        />
        <Route path={HELP} element={<Help />} />
        <Route
          path={ADD_DEVICE}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.DEVICES_MANAGE}>
              <AddDevice />
            </ProtectedRoute>
          }
        />
        <Route
          path={NVR_CAMERA_SYNC}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.DEVICES_MANAGE}>
              <NvrCameraSync />
            </ProtectedRoute>
          }
        />
        <Route
          path={DELETED_DEVICES}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.DEVICES_MANAGE}>
              <DeletedDevices />
            </ProtectedRoute>
          }
        />
        <Route
          path={CATALOGS}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.ORG_SETTINGS}>
              <CatalogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={CATALOG_DEVICE_CATEGORIES}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.ORG_SETTINGS}>
              <DeviceCategoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={CATALOG_DEVICE_MODEL_GROUPS}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.ORG_SETTINGS}>
              <DeviceModelGroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={CATALOG_DEVICE_MODELS}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.ORG_SETTINGS}>
              <DeviceModelsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={MEMBERS}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.USERS_MANAGE}>
              <Members />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${SETTINGS}/*`}
          element={
            <ProtectedRoute permission={PERMISSION_KEYS.ORG_SETTINGS}>
              <Suspense fallback={<SuspenseFallback />}>
                <SettingsModule />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route path={UNAUTHORIZED} element={<NotAuthorized />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}

export default RouterConfig
